// Xbox API service for communicating with the backend wrapper
import config from '@/config/environment';

const API_BASE_URL = config.apiUrl;

interface ApiResponse<T> {
  data?: T;
  error?: string;
}

interface XboxProfile {
  gamertag: string;
  xuid: string;
  gamerScore: number;
  accountTier: string;
  xboxOneRep: string;
  preferredColor: {
    primaryColor: string;
    secondaryColor: string;
    tertiaryColor: string;
  };
  realName: string;
  bio: string;
  location: string;
}

interface XboxFriend {
  gamertag: string;
  xuid: string;
  isOnline: boolean;
  presence: string;
  lastSeenDateTime: string;
  avatarUrl: string;
}

interface PartyMember {
  gamertag: string;
  xuid: string;
  ipAddress: string;
  port: number;
  country: string;
  city: string;
  isp: string;
}

interface GameSessionPlayer {
  gamertag: string;
  xuid: string;
  ipAddress: string;
  port: number;
  country: string;
  city: string;
  isp: string;
}

interface TitleSearchResult {
  titleId: string;
  productId: string;
  name: string;
  shortDescription: string;
  imageUrl: string | null;
  publisher: string;
  category: string;
}

class XboxApiService {
  private accessToken: string | null = null;

  constructor() {
    // Load token from localStorage if available
    this.accessToken = localStorage.getItem('xbox_access_token');
  }

  private async makeRequest<T>(endpoint: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        ...(options.headers as Record<string, string>),
      };

      // For XBL proxy endpoints, send only the encrypted access blob
      if (endpoint.startsWith('/xbl/')) {
        // DEV: Check for raw XBL3.0 token first (local development bypass)
        const rawXblToken = localStorage.getItem('xbox_xbl_token');
        if (rawXblToken) {
          headers['Authorization'] = rawXblToken;
        } else {
          const encrypted = localStorage.getItem('xbox_encrypted_access');
          if (encrypted) headers['X-Access'] = encrypted;
        }
      } else {
        // Non-XBL endpoints can continue to use bearer if applicable
        if (this.accessToken) headers['Authorization'] = `Bearer ${this.accessToken}`;
      }

      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        ...options,
        headers,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        // Include status code in error message for rate limit detection
        const errorMessage = errorData.error || `HTTP ${response.status}`;
        const statusPrefix = response.status === 429 ? '429 Rate Limited: ' : `HTTP ${response.status}: `;
        return { error: statusPrefix + errorMessage };
      }

      const data = await response.json();
      return { data };
    } catch (error) {
      console.error('API request failed:', error);
      return { error: 'Network error or server unavailable' };
    }
  }

  // Authentication methods
  async getLoginUrl(): Promise<ApiResponse<{ authUrl: string }>> {
    return this.makeRequest<{ authUrl: string }>('/auth/login');
  }

  async handleCallback(code: string, state: string): Promise<ApiResponse<any>> {
    const response = await this.makeRequest<any>(`/auth/callback?code=${code}&state=${state}`);
    
    if (response.data) {
      this.accessToken = response.data.accessToken;
      localStorage.setItem('xbox_access_token', response.data.accessToken);
      if (response.data.refreshToken) {
        localStorage.setItem('xbox_refresh_token', response.data.refreshToken);
      }
    }
    
    return response;
  }

  async refreshToken(): Promise<ApiResponse<any>> {
    const refreshToken = localStorage.getItem('xbox_refresh_token');
    if (!refreshToken) {
      return { error: 'No refresh token available' };
    }

    const response = await this.makeRequest<any>('/auth/token', {
      method: 'POST',
      body: JSON.stringify({ refreshToken }),
    });

    if (response.data) {
      this.accessToken = response.data.accessToken;
      localStorage.setItem('xbox_access_token', response.data.accessToken);
      if (response.data.refreshToken) {
        localStorage.setItem('xbox_refresh_token', response.data.refreshToken);
      }
    }

    return response;
  }

  logout(): void {
    this.accessToken = null;
    localStorage.removeItem('xbox_access_token');
    localStorage.removeItem('xbox_refresh_token');
  }

  isAuthenticated(): boolean {
    return !!this.accessToken;
  }

  // Xbox API methods
  // XBL proxy: friends
  async getFriendsViaProxy(): Promise<ApiResponse<any>> {
    return this.makeRequest<any>('/xbl/friends');
  }

  // XBL proxy: party chat sessions for a given xuid
  async getChatSessions(xuid: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(`/xbl/chat-sessions?xuid=${encodeURIComponent(xuid)}`);
  }

  // XBL proxy: party info by partyId (sessionRef name)
  async getPartyInfoById(partyId: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(`/xbl/party/${encodeURIComponent(partyId)}`);
  }

  // XBL proxy: crash party action
  async crashPartyXbl(partyId: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(`/xbl/party/${encodeURIComponent(partyId)}/crash`, { method: 'POST' });
  }

  // Resolve a single XUID to profile settings (avatar, gamertag, gamerscore)
  async resolveXuid(xuid: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(`/xbl/resolve-xuid?xuid=${encodeURIComponent(xuid)}`);
  }

  async resolveXuidsBatch(xuids: string[], settings: string[] = ['Gamertag']): Promise<ApiResponse<{ profileUsers: Array<{ id: string; settings: Array<{ id: string; value: string }> }> }>> {
    return this.makeRequest<{ profileUsers: Array<{ id: string; settings: Array<{ id: string; value: string }> }> }>('/xbl/resolve-xuids', {
      method: 'POST',
      body: JSON.stringify({ xuids, settings }),
    });
  }
  async getProfile(): Promise<ApiResponse<XboxProfile>> {
    return this.makeRequest<XboxProfile>('/xbox/profile');
  }

  async getFriends(): Promise<ApiResponse<{ friends: XboxFriend[]; totalCount: number }>> {
    return this.makeRequest<{ friends: XboxFriend[]; totalCount: number }>('/xbox/friends');
  }

  async getPartyInfo(): Promise<ApiResponse<{ partyId: string; members: PartyMember[]; memberCount: number }>> {
    return this.makeRequest<{ partyId: string; members: PartyMember[]; memberCount: number }>('/xbox/party');
  }

  async crashParty(partyId: string): Promise<ApiResponse<{ success: boolean; message: string; partyId: string; timestamp: string }>> {
    return this.makeRequest<{ success: boolean; message: string; partyId: string; timestamp: string }>('/xbox/party/crash', {
      method: 'POST',
      body: JSON.stringify({ partyId }),
    });
  }

  async getGameSession(): Promise<ApiResponse<{ sessionId: string; gameName: string; gameMode: string; players: GameSessionPlayer[]; playerCount: number }>> {
    return this.makeRequest<{ sessionId: string; gameName: string; gameMode: string; players: GameSessionPlayer[]; playerCount: number }>('/xbox/game-session');
  }

  // Messaging (Inbox)
  async getInbox(): Promise<ApiResponse<any>> {
    return this.makeRequest<any>('/xbl/inbox');
  }

  async getConversationWithUser(xuid: string, maxItems: number = 100): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(`/xbl/conversations/users/${encodeURIComponent(xuid)}?maxItems=${encodeURIComponent(String(maxItems))}`);
  }

  async sendMessageToUser(xuid: string, text: string): Promise<ApiResponse<{ messageId: string; conversationId: string }>> {
    const body = { parts: [{ contentType: 'text', version: 0, text }] } as any;
    return this.makeRequest<{ messageId: string; conversationId: string }>(`/xbl/conversations/users/${encodeURIComponent(xuid)}`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async markConversationRead(conversationId: string, horizon: string): Promise<ApiResponse<any>> {
    const payload = {
      conversations: [
        { conversationId, conversationType: 'OneToOne', horizonType: 'Read', horizon },
      ],
    } as any;
    return this.makeRequest<any>('/xbl/conversations/horizon', {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  async deleteMessage(conversationId: string, messageId: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(`/xbl/conversations/${encodeURIComponent(conversationId)}/messages/${encodeURIComponent(messageId)}`, {
      method: 'DELETE',
    });
  }

  // Delete conversation messages up to a horizon (works for OneToOne or Group)
  async deleteConversationToHorizon(conversationId: string, horizon: string, conversationType: 'OneToOne' | 'Group' = 'OneToOne'): Promise<ApiResponse<any>> {
    const payload = {
      conversations: [
        { conversationId, conversationType, horizonType: 'Delete', horizon },
      ],
    } as any;
    return this.makeRequest<any>('/xbl/conversations/horizon', {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  // Send to Gamertag or XUID via backend resolver
  async sendTo(to: string, text: string): Promise<ApiResponse<{ messageId: string; conversationId: string }>> {
    return this.makeRequest<{ messageId: string; conversationId: string }>(`/xbl/send`, {
      method: 'POST',
      body: JSON.stringify({ to, text }),
    });
  }

  // Validate gamertag exists
  async validateGamertag(gamertag: string): Promise<ApiResponse<{ valid: boolean; gamertag: string; xuid?: string; reason?: string }>> {
    return this.makeRequest<{ valid: boolean; gamertag: string; xuid?: string; reason?: string }>('/xbl/validate-gamertag', {
      method: 'POST',
      body: JSON.stringify({ gamertag }),
    });
  }

  // Get conversation summary
  async getConversationSummary(conversationId: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(`/xbl/conversations/${encodeURIComponent(conversationId)}/summary`);
  }

  // Health check
  async healthCheck(): Promise<ApiResponse<{ status: string; timestamp: string; service: string }>> {
    return this.makeRequest<{ status: string; timestamp: string; service: string }>('/health');
  }

  // Presence spoofing
  async setPresence(titleId: string, state: 'active' | 'inactive' = 'active'): Promise<ApiResponse<any>> {
    return this.makeRequest<any>('/xbl/presence/set', {
      method: 'POST',
      body: JSON.stringify({ titleId, state }),
    });
  }

  async clearPresence(): Promise<ApiResponse<any>> {
    return this.makeRequest<any>('/xbl/presence/clear', {
      method: 'POST',
    });
  }

  async getCurrentPresence(): Promise<ApiResponse<any>> {
    return this.makeRequest<any>('/xbl/presence/current');
  }

  async searchTitles(query: string): Promise<ApiResponse<{ titles: TitleSearchResult[]; totalCount: number }>> {
    return this.makeRequest<{ titles: TitleSearchResult[]; totalCount: number }>(`/xbl/titles/search?query=${encodeURIComponent(query)}`);
  }

  // Profile Inspector APIs - All async/parallel capable
  async profileLookup(identifier: string): Promise<ApiResponse<any>> {
    // Determine if identifier is XUID (all digits, 13-20 chars) or Gamertag
    const isXuid = /^\d{13,20}$/.test(identifier.trim());
    const param = isXuid ? `xuid=${encodeURIComponent(identifier)}` : `gamertag=${encodeURIComponent(identifier)}`;
    return this.makeRequest<any>(`/xbl/profile/lookup?${param}`);
  }

  async profilePresence(xuid: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(`/xbl/profile/presence?xuid=${encodeURIComponent(xuid)}`);
  }

  async profileFriends(xuid: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(`/xbl/profile/friends?xuid=${encodeURIComponent(xuid)}`);
  }

  async profileTitles(xuid: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(`/xbl/profile/titles?xuid=${encodeURIComponent(xuid)}`);
  }

  async profileAchievements(xuid: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(`/xbl/profile/achievements?xuid=${encodeURIComponent(xuid)}`);
  }

  async profileClips(xuid: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(`/xbl/profile/clips?xuid=${encodeURIComponent(xuid)}`);
  }

  async profileScreenshots(xuid: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(`/xbl/profile/screenshots?xuid=${encodeURIComponent(xuid)}`);
  }

  async profileClubs(xuid: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(`/xbl/profile/clubs?xuid=${encodeURIComponent(xuid)}`);
  }

  async profileActivity(xuid: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(`/xbl/profile/activity?xuid=${encodeURIComponent(xuid)}`);
  }

  // Social profile - PeopleHub with detail decoration (follower/following counts, hasGamePass, etc.)
  async profileSocial(xuid: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(`/xbl/profile/social?xuid=${encodeURIComponent(xuid)}`);
  }

  // Resolve gamertag to IP (via Octolus API) with GeoIP enrichment
  async resolveGamertag(gamertag: string): Promise<ApiResponse<ResolverResult>> {
    return this.makeRequest<ResolverResult>(`/xbl/resolve?gamertag=${encodeURIComponent(gamertag)}`);
  }

  // Game Session lookup with IP decryption
  // Looks up active game session for a player and decrypts secureDeviceAddress to get IPs
  async lookupGameSession(identifier: string): Promise<ApiResponse<GameSessionLookupResult>> {
    // Determine if identifier is XUID (all digits, 13-20 chars) or Gamertag
    const isXuid = /^\d{13,20}$/.test(identifier.trim());
    const payload = isXuid 
      ? { xuid: identifier.trim() }
      : { gamertag: identifier.trim() };
    
    return this.makeRequest<GameSessionLookupResult>('/xbl/game-session/lookup', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }
}

// Resolver result interface
export interface ResolverResult {
  resolved: boolean;
  gamertag: string;
  ip?: string;
  lookupDate?: string;
  message?: string;
  geo?: {
    country?: string;
    countryCode?: string;
    region?: string;
    city?: string;
    postalCode?: string;
    latitude?: number;
    longitude?: number;
    timezone?: string;
    isp?: string;
    org?: string;
    asn?: string;
    asnOrg?: string;
  };
}

// Game Session Lookup result interface
export interface GameSessionMember {
  gamertag: string;
  xuid: string;
  ipAddress: string | null;
  port: string | null;
  isYou: boolean;
  connectionId?: string;
  active?: boolean;
  geo?: {
    country?: string;
    countryCode?: string;
    region?: string;
    city?: string;
    isp?: string;
    latitude?: number;
    longitude?: number;
  };
}

export interface GameSessionLookupResult {
  success: boolean;
  sessionId?: string;
  scid?: string;
  templateName?: string;
  titleId?: string;
  visibility?: string;
  members?: GameSessionMember[];
  memberCount?: number;
  error?: string;
  rawSessions?: any;
}

// Create and export a singleton instance
export const xboxApiService = new XboxApiService();

// Export types for use in components
export type {
  XboxProfile,
  XboxFriend,
  PartyMember,
  GameSessionPlayer,
  TitleSearchResult,
  ApiResponse,
};
